import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, IconButton } from 'native-base';
import { NavigationProp, useNavigation } from '@react-navigation/native';

import { ButtonGroup, LightBulbIcon, Text } from '@pimm/base';
import { MainNavigationParams, MainScreenProps } from '@app/navigations/root';
import { MainContainer } from '@app/components/core';
import { ScreenLayout } from '@app/components/layout';
import { ResourceLoader } from '@app/components/shared';
import { ModuleTitle, useAuthPermissions, useAuthToken } from '@app/features/app';
import { useSiteConfig } from '@app/features/store-core';
import {
  DataSyncButton,
  EquipmentConfigProvider,
  EquipmentStatus,
  EquipmentSummary,
  FreshnessLabel,
  useGetDisplayGroups,
  useGetEventTypes,
} from '@app/features/store-equipment';
import { ModalAppDocuments } from '@app/features/store-info/components';
import { compact, isEmpty } from 'lodash';

type TemperatureScreenTabs = 'diagnostics' | 'performance' | 'status';

export type TemperaturesScreenProps = MainScreenProps<'Temperatures'>;

export const TemperaturesScreen: React.FC<TemperaturesScreenProps> = ({ route: { params, ...route } }) => {
  const { t } = useTranslation();
  const { siteConfig } = useSiteConfig();
  const { user } = useAuthToken();
  const permissions = useAuthPermissions(state => state.permissions);
  const [tabFocus, setTabFocus] = useState<TemperatureScreenTabs>((params.tabFocus ?? 'status') as TemperatureScreenTabs);
  const [refreshSignal, setRefreshSignal] = useState(0);

  const displayGroups = useGetDisplayGroups(siteConfig.id);
  const eventTypes = useGetEventTypes(siteConfig.companyInfo?.brandId);

  const siteInfo = useMemo(
    () => ({
      name: siteConfig.name,
      customerName: siteConfig.companyInfo?.customerName,
      address: compact([
        siteConfig.address?.address1,
        siteConfig.address?.city,
        compact([siteConfig.address?.state, siteConfig.address?.postal]).join(' '),
      ]).join(', ') || undefined,
      logoUrl: siteConfig.companyInfo?.brand?.logoImgUrl,
      utcOffsetInMinutes: siteConfig.address?.utcOffsetInMinutes,
    }),
    [siteConfig],
  );

  const generatedBy = user?.username;

  const navigation = useNavigation<NavigationProp<MainNavigationParams, 'Temperatures'>>();

  const handleInstanceChange = useCallback(
    (instanceId?: string) => {
      navigation.setParams({ instanceId, tabFocus: 'status' });
    },
    [navigation],
  );

  const handleTabChange = useCallback(
    (tab: TemperatureScreenTabs) => {
      setTabFocus(tab);
      if (tab !== 'status') {
        navigation.setParams({ instanceId: undefined, tabFocus: tab });
      }
    },
    [navigation],
  );

  const isNothingToDisplay = (displayGroups.isSuccess && isEmpty(displayGroups.data)) || displayGroups.isError;

  return (
    <MainContainer>
      <ScreenLayout
        title={
          <HStack>
            <ModuleTitle name={route.name} defaultText="Temperature" />
            <ModalAppDocuments
              identifierName="Module.Equipment"
              name={route.name}
              moduleType="Equipment"
              trigger={triggerProps => {
                if (!triggerProps.documents?.length) return undefined;
                return (
                  <IconButton
                    rounded="lg"
                    borderWidth={1}
                    borderColor="gray.300"
                    ml={2}
                    p={0}
                    w={9}
                    h={9}
                    icon={<LightBulbIcon size="md" />}
                    onPress={triggerProps.onPress}
                  />
                );
              }}
            />
          </HStack>
        }
        actionTool={
          <HStack space={2} justifyContent="flex-end" alignItems="center">
            <FreshnessLabel
              lastUpdatedAt={displayGroups.dataUpdatedAt}
              isFetching={displayGroups.isFetching}
              onRefresh={() => {
                displayGroups.refetch();
                setRefreshSignal(s => s + 1);
              }}
            />
            <DataSyncButton
              siteId={siteConfig.id}
              canDataSync={!!permissions?.equipmentDevicemode?.write}
              onSynced={() => displayGroups.refetch()}
            />
            <ButtonGroup value={tabFocus} onChange={handleTabChange}>
              <ButtonGroup.Item value="status" minWidth={90}>
                {t('store_equipment:status')}
              </ButtonGroup.Item>
              <ButtonGroup.Item value="diagnostics" isDisabled={isNothingToDisplay}>
                {t('store_equipment:diagnostics')}
              </ButtonGroup.Item>
              <ButtonGroup.Item value="performance" isDisabled={isNothingToDisplay}>
                {t('store_equipment:performance')}
              </ButtonGroup.Item>
            </ButtonGroup>
          </HStack>
        }
      >
        <EquipmentConfigProvider
          eventTypes={eventTypes.data ?? []}
          canEditSensor={!!permissions?.equipmentInstance?.write}
          canDataSync={!!permissions?.equipmentDevicemode?.write}
        >
          <ResourceLoader
            h="full"
            isEmpty={isNothingToDisplay}
            isLoading={displayGroups.isIdle || displayGroups.isLoading}
            emptyMessage={
              displayGroups.isError ? (
                <Text size="lg" fontWeight={600} color="gray.500" textAlign="center">
                  {t('app:no_data_found')}
                </Text>
              ) : (
                <>
                  <Text size="lg" fontWeight={600} color="gray.500" textAlign="center">
                    {t('app:nothing_to_display')}.{`\n`}
                    <Text size="md" fontWeight={400} color="gray.500">
                      {t('app:module_not_setup')}.
                    </Text>
                  </Text>
                </>
              )
            }
          >
            {tabFocus === 'status' ? (
              <EquipmentStatus
                queryDisplayGroups={displayGroups}
                siteId={siteConfig.id}
                initialInstanceId={params.instanceId}
                onChangeInstance={handleInstanceChange}
                siteInfo={siteInfo}
                generatedBy={generatedBy}
              />
            ) : (
              <EquipmentSummary
                key={tabFocus}
                tableMode={tabFocus === 'performance' ? 'performance' : 'diagnostics'}
                queryDisplayGroups={displayGroups}
                siteId={siteConfig.id}
                siteInfo={siteInfo}
                generatedBy={generatedBy}
                refreshSignal={refreshSignal}
              />
            )}
          </ResourceLoader>
        </EquipmentConfigProvider>
      </ScreenLayout>
    </MainContainer>
  );
};
